import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import {
  FDBodyText, FDIcon, FDLozenge, FDPaper, FDStack, FDTooltip,
} from '@facet-wealth/design';
import { currency } from 'Helpers/Formats';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';

const useStyles = buildUseStyles('GuidedHouseholdReviewLineItem', () => ({
  wrapper: {
    display: 'flex',
    flex: 1,
  },
  bold: {
    '& > * > * > *': {
      fontWeight: '500 !important', // Needs to be !important to overwrite FD to match the figma
    },
    '& > * > *': {
      fontWeight: '500 !important', // Needs to be !important to overwrite FD to match the figma
    },
  },
}));

export const GuidedHouseholdReviewLineItem = ({ resource, multiplyBy, viewAnnually }) => {
  const classes = useStyles();
  const { loadModalCollectionByLink } = useModalContext();
  const hasProblem = resource.isTruthy('problem');

  let valueStr = currency(resource.attr('value') * multiplyBy, true);
  if (resource.attr('valueSuffix')) {
    valueStr += ` ${resource.attr('valueSuffix')}`;
  }
  let content = (
    <div className={classNames(classes.wrapper, { [classes.bold]: resource.attr('bold') === 'true' })}>
      <FDStack direction="row" spacing={1} justifyContent="space-between" flex={1}>
        <FDStack direction={'row'} spacing={1} alignItems={'center'}>
          {resource.attr('tag') && (
            <FDLozenge
              color="info"
              label={resource.attr('tag')}
              size="sm"
            />
          )}
          <FDBodyText
            value={
                viewAnnually && resource.attr('annualNameOverride') ? resource.attr('annualNameOverride') : resource.attr('name')
              }
            flex={1}
            color={hasProblem ? 'textError' : undefined}
          />
          {resource.attr('tooltip') ? (
            <FDTooltip title={resource.attr('tooltip')} placement="top">
              <FDIcon size="sm" icon="circleQuestion" />
            </FDTooltip>
          ) : null}
        </FDStack>
        <FDBodyText value={valueStr} color={hasProblem ? 'textError' : undefined} />
      </FDStack>
    </div>
  );

  // Wrap this in paper to emphasize it if we have an edit relationship or if we need to emphasize that there's a problem with it
  const shouldWrapInPaper = resource.withRelationship(
    fr.relationships.edit.name,
    () => true,
    () => hasProblem,
  );
  if (shouldWrapInPaper) {
    content = <FDPaper padding={2} flex={1} color={hasProblem ? 'error' : 'info'} appearance="flat">{content}</FDPaper>;
  }
  // If we're supposed to be able to edit this, we need to also make this be clickable
  return resource.withRelationship(fr.relationships.edit.name,
    (rel) => (
      <a
        className={classes.wrapper}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          loadModalCollectionByLink(rel.link('related').url());
        }}
        href="#"
      >
        {content}
      </a>
    ),
    () => content);
};

GuidedHouseholdReviewLineItem.propTypes = {
  resource: PropTypes.instanceOf(fr.Collection).isRequired,
  multiplyBy: PropTypes.number.isRequired,
};
